import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { HOW_TO_FILE_PATH } from 'constants/paths';
import { Report } from 'graphql-api';
import { createContractorReport } from 'api/reports';
import { reportCurrentStepPath } from 'utils/path';
import Spinner from 'components/spinner';
import { CONTRACTOR_REPORT_TYPE } from 'constants/common';

function DownloadStartedModal({
  report,
  open,
  setOpen,
  enableCreatingContractorReport
}: {
  report: Report;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  enableCreatingContractorReport: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState<boolean>(false);

  function handleContinue(): void {
    navigate(
      `${HOW_TO_FILE_PATH}${
        report.reportType === CONTRACTOR_REPORT_TYPE ? `?currentTab=${CONTRACTOR_REPORT_TYPE}` : ''
      }`
    );
  }

  function handleCreateContractorReport(): void {
    void (async () => {
      setProcessing(true);
      if (enableCreatingContractorReport) {
        const contractorReport = await createContractorReport();
        if (contractorReport !== null && contractorReport !== undefined) {
          navigate(reportCurrentStepPath(contractorReport));
        } else {
          setProcessing(true);
          alert('Create contractor report unsuccessfully');
        }
      }
    })();
  }
  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-6 pt-6 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                      <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                    </div>
                    <div className="mt-4 text-center">
                      <Dialog.Title
                        as="h3"
                        className="text-xl font-medium leading-8 text-gray-900 mb-2">
                        Download started
                      </Dialog.Title>

                      <div className="mt-2 px-3 text-sm text-gray-700 leading-5">
                        {enableCreatingContractorReport ? (
                          <div className="mb-2">
                            <span className="font-semibold">
                              Before you file your payroll employee report&nbsp;
                            </span>
                            - Did you have 100 or more labor contractor employees within 2022?
                            <br />
                            <br />
                            <span
                              className="underline"
                              data-tooltip-place="bottom"
                              data-tooltip-id="labor-contractor-employee-tooltip">
                              What is a labor contractor employee?
                            </span>
                          </div>
                        ) : (
                          'The following screen contains detailed filing instructions to help you successfully upload your completed reports.'
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:gap-2 w-fit m-auto font-semibold">
                    {enableCreatingContractorReport && (
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={processing}
                        onClick={handleCreateContractorReport}>
                        Yes, I need a labor contractor report
                        {processing && <Spinner className="ml-2" />}
                      </button>
                    )}
                    {enableCreatingContractorReport ? (
                      <button type="button" className="btn-secondary" onClick={handleContinue}>
                        No, continue to file{' '}
                        {report.reportType === CONTRACTOR_REPORT_TYPE ? 'contractor' : 'employee'}{' '}
                        report
                      </button>
                    ) : (
                      <button type="button" className="btn-primary" onClick={handleContinue}>
                        Continue to file{' '}
                        {report.reportType === CONTRACTOR_REPORT_TYPE ? 'contractor' : 'employee'}{' '}
                        report
                      </button>
                    )}
                  </div>
                  {enableCreatingContractorReport && (
                    <div className="text-xs text-gray-700 text-center mt-6">
                      If you created an Employee Report and need to create a Labor Contractor Report
                      now, your Employee Report will be accessible from your dashboard later.
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <Tooltip
        id="labor-contractor-employee-tooltip"
        html="A “labor contractor employee” is an individual on a labor contractor’s payroll, including a part-time individual, and for whom the labor contractor is required to withhold federal social security taxes from that individual’s wages, and who performs labor for a client employer within the client employer’s usual course of business. Not a 1099 independent contractor.<br /><br />A “labor contractor” is an individual or entity that supplies, either with or without a contract, a client employer with workers to perform labor within the client employer’s usual course of business."
        className="bg-gray-900 text-white w-96 rounded px-2 py-1 text-sm z-40 text-center"
      />
    </>
  );
}

export default DownloadStartedModal;
